import React, { useEffect, useState, useRef } from 'react';
import { useRouter } from 'next/router';
import _ from 'lodash';
import { connect } from 'react-redux';
import { BaseStore } from 'src/store';
import { setEnabledCookieGroups as setEnabledCookieGroupsAction } from 'src/modules/cookies';
import ReactGA from 'react-ga4';
import TagManager from 'react-gtm-module';

// import { fetchAnalyticsIds } from 'src/utils/analytics';
import styles from './Cookies.module.scss';
import { OutputChannels, useLocale } from 'src/modules/locale';
import { EnabledCookieGroups } from 'src/modules/cookies';
import analyticIds from 'public/data/analytics.json';

type ChannelPlusLocale = 'de_DE_en' | 'de_DE_de' | 'en_AU_en' | 'en_US_en';

const FACEBOOK_PIXEL_DE_EN = '397794845266441';
const FACEBOOK_PIXEL_US = '539792357317175';
const FACEBOOK_PIXEL_AU = '4395178360532261';
const FACEBOOK_PIXEL_DE_DE = '1952753411710719';

const GTM_DE_EN = 'GTM-NHVCM9V';
const GTM_US = 'GTM-NFSNPM8';
const GTM_AU = 'GTM-5JDNVXD';
const GTM_DE_DE = 'GTM-K4VFRLX';

// Cannot import ReactPixel directly, it requires window to be defined
let ReactPixel = {
  pageView: _.noop,
  init: _.noop,
  __waitingToLoad: true,
};

const getCookieGroups = () => {
  const cookieGroups: EnabledCookieGroups = {
    initial: false,
    targeting: false,
    performance: false,
    functional: false,
  };

  if (typeof window !== 'object') return cookieGroups;
  // @ts-ignore
  if (!window.OnetrustActiveGroups) return cookieGroups;

  // @ts-ignore
  const activeGroups = _.compact(window.OnetrustActiveGroups.split(','));

  _.each(activeGroups, (group) => {
    if (group === 'C0001') cookieGroups.initial = true;
    else if (group === 'C0002') cookieGroups.performance = true;
    else if (group === 'C0003') cookieGroups.functional = true;
    else if (group === 'C0004') cookieGroups.targeting = true;
  });

  return cookieGroups;
};

const handleRouteChange = () => {
  ReactGA.send('pageview');
  console.log('Facebook Pixel - Trigger page view');
  ReactPixel?.pageView();
};

let __initGtm__gaId = '';
let __init__facebookPixelId = '';

/**
 * Initialize Google Analytics and Facebook Pixel scripts
 */
const initPerformanceScripts = _.debounce((outputChannel: string, locale: string) => {
  const gaIds = analyticIds;
  const { walterk_ga, walterknoll_ga, walterknoll_ga_en, walterknoll_ga_au } =
    gaIds?.analytic || {};
  let gaId: string;
  let facebookPixelId: string;
  let gtmId: string;

  const channelPlusLocale = `${outputChannel}_${locale}` as ChannelPlusLocale;

  switch (channelPlusLocale) {
    case 'de_DE_en':
      gaId = walterknoll_ga_en;
      gtmId = GTM_DE_EN;
      facebookPixelId = FACEBOOK_PIXEL_DE_EN;
      break;
    case 'en_US_en':
      gaId = walterk_ga;
      gtmId = GTM_US;
      facebookPixelId = FACEBOOK_PIXEL_US;
      break;
    case 'en_AU_en':
      gaId = walterknoll_ga_au;
      gtmId = GTM_AU;
      facebookPixelId = FACEBOOK_PIXEL_AU;
      break;
    case 'de_DE_de':
    default:
      gaId = walterknoll_ga;
      gtmId = GTM_DE_DE;
      facebookPixelId = FACEBOOK_PIXEL_DE_DE;
      break;
  }
  console.log('channelPlusLocale=', channelPlusLocale);

  // don't initialize more than once for the same gaId
  if (gaId === __initGtm__gaId || !gaId) {
    console.log('Skipping GA initialize');
  } else {
    __initGtm__gaId = gaId;
    console.log('GA and GTM initialized with ids:', gaId, gtmId);
    ReactGA.initialize(gaId);
    TagManager.initialize({ gtmId });
  }

  // don't initialize more than once for the same facebookPixelId
  if (facebookPixelId === __init__facebookPixelId || !facebookPixelId) {
    console.log('Skipping Facebook Pixel initialize');
  } else if (!ReactPixel?.__waitingToLoad) {
    __init__facebookPixelId = facebookPixelId;
    console.log('Facebook Pixel initialized with id:', facebookPixelId);
    ReactPixel?.init(facebookPixelId);
  }

  handleRouteChange();
}, 500);

const Cookies: React.FunctionComponent<any> = ({ enabledCookieGroups, setEnabledCookieGroups }) => {
  const [isAlertBoxClosed, setIsAlertBoxClosed] = useState(true);
  const router = useRouter();
  const { locale, outputChannel } = useLocale();
  const isMounted = useRef(false);

  useEffect(() => {
    // Cannot import ReactPixel directly, it requires window to be defined
    ReactPixel = require('react-facebook-pixel')?.default;
    isMounted.current = true;
  }, []);

  // Initialise OneTrust cookies and setup OptanonWrapper callback function
  useEffect(() => {
    if (typeof window !== 'object') return;

    // Already initialized
    // @ts-ignore
    if (window.OptanonWrapper) return;

    // @ts-ignore
    window.OptanonWrapper = () => {
      const cookieGroups = getCookieGroups();

      // @ts-ignore
      setIsAlertBoxClosed(window.OneTrust.IsAlertBoxClosed());
      setEnabledCookieGroups(cookieGroups);
    };
  }, [setEnabledCookieGroups]);

  useEffect(() => {
    const cookieGroups = getCookieGroups();
    setEnabledCookieGroups(cookieGroups);
  }, [setEnabledCookieGroups]);

  useEffect(() => {
    if (isAlertBoxClosed) return;
    if (outputChannel !== OutputChannels.DE) return;

    // hack: set privacy link with correct locale
    const privacyLink = document.querySelector('#onetrust-policy-text a') as HTMLLinkElement;
    if (privacyLink) {
      privacyLink.href = `/${locale}/privacy`;
    }
  }, [isAlertBoxClosed, locale, outputChannel]);

  useEffect(() => {
    if (enabledCookieGroups.performance && isMounted) {
      initPerformanceScripts(outputChannel, locale);
      // handleRouteChange()
    }
  }, [enabledCookieGroups.performance, enabledCookieGroups, locale, outputChannel]);

  /**
   * Emit Google Analytics page views events
   */
  useEffect(() => {
    router.events.on('routeChangeComplete', handleRouteChange);

    return () => {
      router.events.off('routeChangeComplete', handleRouteChange);
    };
  }, [router.events]);

  return <div className={isAlertBoxClosed ? '' : styles.overlay} />;
};

const mapDispatchToProps = (dispatch: any) => {
  return {
    setEnabledCookieGroups: (cookieGroups: EnabledCookieGroups) =>
      dispatch(setEnabledCookieGroupsAction(cookieGroups)),
  };
};

const mapStateToProps = (state: BaseStore) => {
  return {
    hasHeroBox: !!state?.header?.heroBox,
    enabledCookieGroups: state.cookies.enabledCookieGroups,
  };
};

export default connect(mapStateToProps, mapDispatchToProps)(Cookies);
